import { CircleHelp, RotateCcw, X } from "lucide-react";
import { useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

import { useHelpChat } from "../hooks/useHelpChat";
import { useHelpContext } from "../hooks/useHelpContext";
import { HelpChat } from "./HelpChat";

/**
 * Botao flutuante de ajuda (canto inferior direito) + painel recolhivel de
 * chat com o assistente da plataforma. Montado no ``AppShell`` -- so aparece
 * em telas autenticadas.
 *
 * Renderizado via ``createPortal`` no ``document.body`` e ancorado por estilo
 * inline (``position: fixed`` no canto inferior direito) para nao depender do
 * layout flex do AppShell nem de algum ancestral com ``transform`` -- assim o
 * widget fica sempre colado no canto inferior direito da viewport.
 *
 * O estado da conversa vive aqui (efemero); recolher o painel nao perde a
 * conversa, "nova conversa" (reset) limpa.
 */
export function HelpWidget() {
  const { t } = useTranslation("help");
  const [open, setOpen] = useState(false);
  const screen = useHelpContext();
  const { messages, streaming, send, reset } = useHelpChat(screen);

  const widget = (
    <div
      className="flex flex-col items-end gap-3"
      style={{ position: "fixed", right: "1rem", bottom: "1rem", zIndex: 50 }}
    >
      {open ? (
        <div
          role="dialog"
          aria-label={t("title")}
          className={cn(
            "flex flex-col overflow-hidden rounded-xl border border-line",
            "bg-card shadow-xl",
            "w-[min(24rem,calc(100vw-2rem))]",
            "h-[min(32rem,calc(100vh-7rem))]",
          )}
        >
          <header className="flex items-center justify-between gap-2 border-b px-4 py-3">
            <span className="text-sm font-semibold">{t("title")}</span>
            <div className="flex items-center gap-1">
              <Button
                size="icon"
                variant="ghost"
                onClick={reset}
                title={t("newChat")}
                aria-label={t("newChat")}
              >
                <RotateCcw className="h-4 w-4" />
              </Button>
              <Button
                size="icon"
                variant="ghost"
                onClick={() => setOpen(false)}
                title={t("collapse")}
                aria-label={t("collapse")}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </header>
          <div className="min-h-0 flex-1 p-3">
            <HelpChat messages={messages} streaming={streaming} onSend={send} />
          </div>
        </div>
      ) : null}

      <Button
        size="icon"
        onClick={() => setOpen((v) => !v)}
        aria-label={t("button")}
        title={t("button")}
        className="h-12 w-12 rounded-full shadow-lg"
      >
        <CircleHelp className="h-6 w-6" />
      </Button>
    </div>
  );

  return createPortal(widget, document.body);
}
